import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import ReactQuill from 'react-quill-new';
import 'react-quill/dist/quill.snow.css';
import Spinner from '../../components/Spinner';
import { 
    DocumentTextIcon, 
    PhotoIcon, 
    TagIcon, 
    ChartBarIcon,
    GlobeAltIcon,
    EyeIcon,
    ClockIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';

const ArticleEditPage = () => {
    const quillRef = React.useRef(null);
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    // Basic Fields
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [coverImageUrl, setCoverImageUrl] = useState('');
    const [pageType, setPageType] = useState('');
    const [category, setCategory] = useState('');
    const [tags, setTags] = useState('');
    const [status, setStatus] = useState('');
    const [featured, setFeatured] = useState(false);
    const [readTime, setReadTime] = useState(5);
    
    // SEO Fields
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [metaKeywords, setMetaKeywords] = useState('');
    const [slug, setSlug] = useState('');
    
    // Social Media Fields
    const [ogImage, setOgImage] = useState('');
    const [ogTitle, setOgTitle] = useState('');
    const [ogDescription, setOgDescription] = useState('');
    
    const [activeTab, setActiveTab] = useState('content');

    const navigate = useNavigate();
    const { token } = useContext(AuthContext);

    useEffect(() => {
        const fetchArticle = async () => {
            if (!token || !id) return;
            setLoading(true);
            try {
                const config = { headers: { 'x-auth-token': token } };
                const res = await axios.get(`${API_BASE_URL}/api/articles/editor/${id}`, config);
                const article = res.data;
                
                setTitle(article.title);
                setContent(article.content);
                setExcerpt(article.excerpt);
                setCoverImageUrl(article.coverImageUrl);
                setPageType(article.pageType || '');
                setCategory(article.category || '');
                setTags(Array.isArray(article.tags) ? article.tags.join(', ') : '');
                setStatus(article.status || 'draft');
                setFeatured(article.featured || false);
                setReadTime(article.readTime || 5);
                
                // SEO Fields
                setMetaTitle(article.metaTitle || '');
                setMetaDescription(article.metaDescription || '');
                setMetaKeywords(Array.isArray(article.metaKeywords) ? article.metaKeywords.join(', ') : '');
                setSlug(article.slug || '');
                
                // Social Media
                setOgImage(article.ogImage || '');
                setOgTitle(article.ogTitle || '');
                setOgDescription(article.ogDescription || '');
            } catch (err) {
                toast.error('Failed to load article.');
                navigate('/editor/articles');
            } finally { 
                setLoading(false); 
            }
        };
        fetchArticle();
    }, [id, token, navigate]);

    const calculateReadTime = (text) => {
        const wordsPerMinute = 200;
        const words = text.replace(/<[^>]*>/g, '').split(/\s+/).length;
        return Math.ceil(words / wordsPerMinute) || 1;
    };

    const handleContentChange = (value) => {
        // Clean up excessive &nbsp; entities
        const cleanValue = value.replace(/(&nbsp;)+/g, ' ');
        setContent(cleanValue);
        const calculatedTime = calculateReadTime(cleanValue);
        setReadTime(calculatedTime);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        if (!pageType) {
            return toast.error('Please select a page type (News, Research, Info).');
        }
        if (!title || !content || !excerpt || !coverImageUrl) {
            return toast.error('Please fill in all required fields.');
        }

        setSubmitting(true);
        const loadingToast = toast.loading('Updating content...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            const body = { 
                title, 
                content, 
                excerpt, 
                coverImageUrl, 
                pageType, 
                category: category || 'General',
                status, 
                tags,
                featured,
                readTime,
                metaTitle: metaTitle || title,
                metaDescription: metaDescription || excerpt,
                metaKeywords,
                slug,
                ogImage: ogImage || coverImageUrl,
                ogTitle: ogTitle || title,
                ogDescription: ogDescription || excerpt
            };

            await axios.put(`${API_BASE_URL}/api/articles/${id}`, body, config);

            toast.success('Content updated successfully!', { id: loadingToast });
            navigate('/editor/articles');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Error updating content.', { id: loadingToast });
            setSubmitting(false);
        }
    };

    const videoHandler = () => {
        let url = prompt("Enter Video URL (YouTube/Vimeo) or Embed Code:");
        if (!url) return;

        if (url.includes('<iframe')) {
            const srcMatch = url.match(/src=["'](.*?)["']/);
            if (srcMatch && srcMatch[1]) {
                url = srcMatch[1];
            }
        }
        
        let embedUrl = url;
        if (url.includes('youtube.com/watch?v=')) {
            const videoId = url.split('v=')[1].split('&')[0];
            embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('youtu.be/')) {
            const videoId = url.split('youtu.be/')[1].split('?')[0];
            embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('youtube.com/shorts/')) {
            const videoId = url.split('shorts/')[1].split('?')[0];
            embedUrl = `https://www.youtube.com/embed/${videoId}`;
        }

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'video', embedUrl);
        editor.setSelection(range.index + 1);
    };

    const quillModules = React.useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline','strike', 'blockquote', 'code-block'],
                [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                [{ 'align': [] }],
                [{ 'color': [] }, { 'background': [] }],
                ['link', 'image', 'video'],
                ['clean']
            ],
            handlers: {
                video: videoHandler
            }
        },
        clipboard: {
            matchVisual: false
        }
    }), []);

    const tabs = [
        { id: 'content', label: 'Content', icon: DocumentTextIcon },
        { id: 'seo', label: 'SEO', icon: ChartBarIcon },
        { id: 'social', label: 'Social Media', icon: GlobeAltIcon },
        { id: 'settings', label: 'Settings', icon: SparklesIcon }
    ];

    if (loading) return <Spinner />;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-7xl mx-auto pb-8"
        >
            {/* Header */}
            <div className="card p-6 mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">Edit Content</h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Update your content with SEO optimization and social media integration
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button 
                            type="button" 
                            onClick={() => navigate('/editor/articles')} 
                            className="btn btn-secondary"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={onSubmit} 
                            disabled={submitting} 
                            className="btn btn-primary flex items-center gap-2"
                        >
                            {submitting ? <Spinner size='h-5 w-5 border-b-2' /> : 'Update Content'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="card mb-6">
                <div className="flex border-b border-light-border dark:border-dark-border overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all whitespace-nowrap ${
                                activeTab === tab.id
                                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                        >
                            <tab.icon className="h-5 w-5" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <form onSubmit={onSubmit}>
                {/* Content Tab */}
                {activeTab === 'content' && (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-6"
                    >
                        {/* Title & Page Type */}
                        <div className="card p-6">
                            <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                                <DocumentTextIcon className="h-5 w-5" />
                                Article Title *
                            </label>
                            <input 
                                type="text" 
                                placeholder="Enter a compelling title..." 
                                value={title} 
                                onChange={(e) => setTitle(e.target.value)} 
                                required 
                                className="input-field !text-3xl !font-bold mb-4" 
                            />
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold mb-2">Content Type *</label>
                                    <select 
                                        value={pageType} 
                                        onChange={(e) => setPageType(e.target.value)} 
                                        required 
                                        className="input-field"
                                    >
                                        <option value="">-- Select Type --</option>
                                        <option value="News">📰 News</option>
                                        <option value="Research">🔬 Research</option>
                                        <option value="Info">ℹ️ Info</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-2">Category</label>
                                    <input 
                                        type="text" 
                                        placeholder="e.g., Rocket Science" 
                                        value={category} 
                                        onChange={(e) => setCategory(e.target.value)} 
                                        className="input-field" 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Main Content Editor */}
                        <div className="card p-6">
                            <label className="block text-sm font-semibold mb-2">Main Content *</label>
                            <div className="bg-white dark:bg-dark-bg rounded-lg border border-light-border dark:border-dark-border min-h-[400px]">
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={content}
                                    onChange={handleContentChange}
                                    modules={quillModules}
                                    className="h-[350px] mb-12 md:mb-0"
                                    placeholder="Start writing your amazing content..."
                                />
                            </div>
                        </div>

                        {/* Excerpt */}
                        <div className="card p-6">
                            <label className="block text-sm font-semibold mb-2">Excerpt / Summary *</label>
                            <textarea 
                                placeholder="A brief summary of your content (2-3 sentences)..." 
                                value={excerpt} 
                                onChange={(e) => setExcerpt(e.target.value)} 
                                required 
                                rows="4" 
                                className="input-field mt-12"
                                maxLength="300"
                            />
                            <p className="text-sm text-gray-500 mt-1">{excerpt.length}/300 characters</p>
                        </div>

                        {/* Media */}
                        <div className="card p-6">
                            <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                                <PhotoIcon className="h-5 w-5" />
                                Cover Image *
                            </label>
                            <input 
                                type="url" 
                                placeholder="https://example.com/image.jpg" 
                                value={coverImageUrl} 
                                onChange={(e) => setCoverImageUrl(e.target.value)} 
                                required 
                                className="input-field mb-3" 
                            />
                            {coverImageUrl && (
                                <div className="mt-3 rounded-lg overflow-hidden border border-light-border dark:border-dark-border">
                                    <img 
                                        src={coverImageUrl} 
                                        alt="Preview" 
                                        className="w-full h-64 object-cover"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Tags */}
                        <div className="card p-6">
                            <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                                <TagIcon className="h-5 w-5" />
                                Tags
                            </label>
                            <input 
                                type="text" 
                                placeholder="space, astronomy, rocket, nasa (comma separated)" 
                                value={tags} 
                                onChange={(e) => setTags(e.target.value)} 
                                className="input-field" 
                            />
                            <p className="text-sm text-gray-500 mt-1">Add relevant tags separated by commas</p>
                        </div>
                    </motion.div>
                )}

                {/* SEO Tab */}
                {activeTab === 'seo' && (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-6"
                    >
                        <div className="card p-6">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <ChartBarIcon className="h-6 w-6" />
                                Search Engine Optimization
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                Optimize your content for search engines to improve visibility and ranking
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold mb-2">Meta Title</label>
                                    <input 
                                        type="text" 
                                        placeholder="SEO-friendly title" 
                                        value={metaTitle} 
                                        onChange={(e) => setMetaTitle(e.target.value)} 
                                        className="input-field" 
                                        maxLength="60"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">{metaTitle.length}/60 characters - Recommended: 50-60</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Meta Description</label>
                                    <textarea 
                                        placeholder="Brief description for search results" 
                                        value={metaDescription} 
                                        onChange={(e) => setMetaDescription(e.target.value)} 
                                        rows="3" 
                                        className="input-field"
                                        maxLength="160"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">{metaDescription.length}/160 characters - Recommended: 150-160</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Meta Keywords</label>
                                    <input 
                                        type="text" 
                                        placeholder="keyword1, keyword2, keyword3" 
                                        value={metaKeywords} 
                                        onChange={(e) => setMetaKeywords(e.target.value)} 
                                        className="input-field" 
                                    />
                                    <p className="text-sm text-gray-500 mt-1">Separate keywords with commas</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">URL Slug</label>
                                    <input 
                                        type="text" 
                                        placeholder="article-url-slug" 
                                        value={slug} 
                                        onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} 
                                        className="input-field font-mono" 
                                    />
                                    <p className="text-sm text-gray-500 mt-1">
                                        Preview: /articles/{slug || 'your-article-slug'}
                                    </p>
                                </div>
                            </div>

                            {/* SEO Preview */}
                            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <h3 className="text-sm font-semibold mb-3">Search Engine Preview</h3>
                                <div className="space-y-1">
                                    <div className="text-blue-600 dark:text-blue-400 text-lg hover:underline cursor-pointer">
                                        {metaTitle || title || 'Your Article Title'}
                                    </div>
                                    <div className="text-green-700 dark:text-green-500 text-sm">
                                        www.yoursite.com/articles/{slug || 'article-slug'}
                                    </div>
                                    <div className="text-gray-600 dark:text-gray-400 text-sm">
                                        {metaDescription || excerpt || 'Your article description will appear here...'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Social Media Tab */}
                {activeTab === 'social' && (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-6"
                    >
                        <div className="card p-6">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <GlobeAltIcon className="h-6 w-6" />
                                Social Media Optimization
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                Customize how your content appears when shared on social media platforms
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold mb-2">Open Graph Title</label>
                                    <input 
                                        type="text" 
                                        placeholder="Title for social media" 
                                        value={ogTitle} 
                                        onChange={(e) => setOgTitle(e.target.value)} 
                                        className="input-field" 
                                        maxLength="60"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">{ogTitle.length}/60 characters</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Open Graph Description</label>
                                    <textarea 
                                        placeholder="Description for social media" 
                                        value={ogDescription} 
                                        onChange={(e) => setOgDescription(e.target.value)} 
                                        rows="3" 
                                        className="input-field"
                                        maxLength="160"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">{ogDescription.length}/160 characters</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Open Graph Image</label>
                                    <input 
                                        type="url" 
                                        placeholder="https://example.com/social-image.jpg" 
                                        value={ogImage} 
                                        onChange={(e) => setOgImage(e.target.value)} 
                                        className="input-field" 
                                    />
                                    <p className="text-sm text-gray-500 mt-1">Recommended: 1200x630px</p>
                                    {ogImage && (
                                        <div className="mt-3 rounded-lg overflow-hidden border border-light-border dark:border-dark-border">
                                            <img 
                                                src={ogImage} 
                                                alt="OG Preview" 
                                                className="w-full h-48 object-cover"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Social Preview */}
                            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <h3 className="text-sm font-semibold mb-3">Social Media Preview</h3>
                                <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                                    {(ogImage || coverImageUrl) && (
                                        <img 
                                            src={ogImage || coverImageUrl} 
                                            alt="Social preview" 
                                            className="w-full h-64 object-cover"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    )}
                                    <div className="p-4">
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                                            YOURSITE.COM
                                        </div>
                                        <div className="font-semibold text-lg mb-1">
                                            {ogTitle || title || 'Your Article Title'}
                                        </div>
                                        <div className="text-gray-600 dark:text-gray-400 text-sm">
                                            {ogDescription || excerpt || 'Your article description...'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-6"
                    >
                        <div className="card p-6">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <SparklesIcon className="h-6 w-6" />
                                Publishing Settings
                            </h2>

                            <div className="space-y-6">
                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2">Publication Status</label>
                                    <select 
                                        value={status} 
                                        onChange={(e) => setStatus(e.target.value)} 
                                        className="input-field"
                                    >
                                        <option value="draft">📝 Draft - Save without publishing</option>
                                        <option value="published">✅ Published - Make live immediately</option>
                                    </select>
                                </div>

                                {/* Featured */}
                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                    <div>
                                        <label className="font-semibold flex items-center gap-2">
                                            <SparklesIcon className="h-5 w-5" />
                                            Featured Article
                                        </label>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Highlight this article on the homepage
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={featured} 
                                            onChange={(e) => setFeatured(e.target.checked)}
                                            className="sr-only peer" 
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>

                                {/* Read Time */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                                        <ClockIcon className="h-5 w-5" />
                                        Estimated Read Time
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input 
                                            type="number" 
                                            value={readTime} 
                                            onChange={(e) => setReadTime(parseInt(e.target.value) || 1)} 
                                            min="1"
                                            className="input-field w-24" 
                                        />
                                        <span className="text-gray-600 dark:text-gray-400">minutes</span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Auto-calculated based on content length
                                    </p>
                                </div>

                                {/* Publishing Info */}
                                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                                        <EyeIcon className="h-5 w-5" />
                                        Publishing Information
                                    </h3>
                                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                        <p>• Content will be {status === 'published' ? 'published immediately' : 'saved as draft'}</p>
                                        <p>• Appears in: {pageType || 'Not selected'} section</p>
                                        <p>• Category: {category || 'General'}</p>
                                        {featured && <p>• Will be featured on homepage</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </form>
        </motion.div>
    );
};

export default ArticleEditPage;